import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/StatusBadge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { formatDate } from "@/lib/helpers";
import { Plus, FileText, Trash2, Send } from "lucide-react";
import { toast } from "sonner";

const emptyFormTemplate = { title: "", description: "", category: "general", requires_signature: true, fields: [] };

export default function Forms() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isStaff = user?.role === "admin" || user?.role === "office_staff";
  const [forms, setForms] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(emptyFormTemplate);
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignStudentId, setAssignStudentId] = useState("");

  const load = useCallback(async () => {
    try {
      const promises = [api.get("/forms"), api.get("/form-submissions")];
      if (isStaff) promises.push(api.get("/students"));
      const results = await Promise.all(promises);
      setForms(results[0].data);
      setSubmissions(results[1].data);
      if (isStaff) setStudents(results[2].data);
    } catch {
      toast.error("Could not load forms");
    } finally {
      setLoading(false);
    }
  }, [isStaff]);

  useEffect(() => {
    load();
  }, [load]);

  const addField = () => setForm((f) => {
    const uid = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    return { ...f, fields: [...f.fields, { uid, key: `field_${f.fields.length + 1}`, label: "", type: "text" }] };
  });
  const updateField = (idx, key, value) => setForm((f) => {
    const fields = [...f.fields];
    fields[idx] = { ...fields[idx], [key]: value };
    return { ...f, fields };
  });
  const removeField = (idx) => setForm((f) => ({ ...f, fields: f.fields.filter((_, i) => i !== idx) }));

  const submitForm = async () => {
    if (!form.title) {
      toast.error("Form title is required");
      return;
    }
    try {
      const payload = { ...form, fields: form.fields.map(({ uid, ...rest }) => rest) };
      await api.post("/forms", payload);
      toast.success("Form template created");
      setCreateOpen(false);
      setForm(emptyFormTemplate);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create form");
    }
  };

  const submitAssign = async () => {
    if (!assignStudentId) {
      toast.error("Select a student");
      return;
    }
    try {
      await api.post("/form-submissions/assign", { form_id: assignTarget.id, student_id: assignStudentId });
      toast.success("Form assigned to student");
      setAssignTarget(null);
      setAssignStudentId("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not assign form");
    }
  };

  if (loading) return <Skeleton className="h-64 rounded-[var(--radius)]" />;

  return (
    <div className="space-y-5" data-testid="forms-page">
      <Tabs defaultValue={isStaff ? "templates" : "my-forms"}>
        <TabsList>
          {isStaff && <TabsTrigger value="templates" data-testid="forms-tab-templates">Form Templates</TabsTrigger>}
          <TabsTrigger value={isStaff ? "submissions" : "my-forms"} data-testid="forms-tab-submissions">
            {isStaff ? "All Submissions" : "My Forms"}
          </TabsTrigger>
        </TabsList>

        {isStaff && (
          <TabsContent value="templates" className="space-y-4 pt-4">
            <div className="flex justify-end">
              <Button onClick={() => setCreateOpen(true)} className="bg-[hsl(var(--role-accent))] hover:opacity-95" data-testid="create-form-button">
                <Plus className="h-4 w-4 mr-2" /> New Form Template
              </Button>
            </div>
            {forms.length === 0 ? (
              <div className="rounded-[var(--radius)] border bg-card p-12 text-center space-y-2" data-testid="forms-empty-state">
                <FileText className="h-8 w-8 mx-auto text-muted-foreground" />
                <p className="text-sm text-muted-foreground">No form templates yet, e.g. Enrollment Agreement, Liability Waiver.</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {forms.map((f) => (
                  <div key={f.id} className="rounded-[var(--radius)] border bg-card p-4 sm:p-5 space-y-2" data-testid="form-template-card">
                    <p className="font-semibold">{f.title}</p>
                    <p className="text-xs text-muted-foreground">{f.description}</p>
                    <p className="text-xs text-muted-foreground capitalize">Category: {f.category}</p>
                    <Button size="sm" variant="outline" onClick={() => setAssignTarget(f)} data-testid="assign-form-button">
                      <Send className="h-3.5 w-3.5 mr-1" /> Assign to Student
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        )}

        <TabsContent value={isStaff ? "submissions" : "my-forms"} className="space-y-3 pt-4">
          {submissions.length === 0 ? (
            <div className="rounded-[var(--radius)] border bg-card p-12 text-center text-sm text-muted-foreground" data-testid="submissions-empty-state">
              No forms to show yet.
            </div>
          ) : (
            <div className="divide-y rounded-[var(--radius)] border bg-card">
              {submissions.map((s) => (
                <div key={s.id} className="flex items-center justify-between gap-3 p-4 sm:p-5" data-testid="form-submission-row">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{s.form_title}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {s.student_name} {s.signed_at ? `· Signed ${formatDate(s.signed_at)}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <StatusBadge status={s.status} />
                    {s.status === "pending" && !isStaff && (
                      <Button size="sm" onClick={() => navigate(`/forms/${s.id}/fill`)} data-testid="complete-form-button">Complete</Button>
                    )}
                    {s.status === "completed" && s.signed_pdf_url && (
                      <a href={`${process.env.REACT_APP_BACKEND_URL}${s.signed_pdf_url}`} target="_blank" rel="noreferrer" className="text-sm text-[hsl(var(--role-accent))] font-medium" data-testid="download-signed-form-link">
                        View PDF
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg" data-testid="create-form-dialog">
          <DialogHeader>
            <DialogTitle>New Form Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <div className="space-y-1.5">
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. Liability Waiver" data-testid="form-template-title-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="form-template-description-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <select className="w-full h-11 rounded-[var(--radius)] border bg-background px-3 text-sm" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
                <option value="general">General</option>
                <option value="enrollment">Enrollment Agreement</option>
                <option value="liability">Liability Waiver</option>
                <option value="consent">Parent Consent</option>
                <option value="handbook">Student Handbook</option>
                <option value="medical">Medical Information</option>
              </select>
            </div>
            <div className="flex items-center justify-between border rounded-[var(--radius)] p-3">
              <Label>Requires signature</Label>
              <Switch checked={form.requires_signature} onCheckedChange={(v) => setForm((f) => ({ ...f, requires_signature: v }))} data-testid="form-template-signature-switch" />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Fields</Label>
                <Button type="button" size="sm" variant="outline" onClick={addField} data-testid="form-template-add-field-button">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add Field
                </Button>
              </div>
              {form.fields.map((field, idx) => (
                <div key={field.uid} className="flex items-center gap-2">
                  <Input placeholder="Field label" value={field.label} onChange={(e) => updateField(idx, "label", e.target.value)} data-testid="form-template-field-label-input" />
                  <select className="h-11 rounded-[var(--radius)] border bg-background px-2 text-sm" value={field.type} onChange={(e) => updateField(idx, "type", e.target.value)}>
                    <option value="text">Text</option>
                    <option value="textarea">Long Text</option>
                    <option value="date">Date</option>
                    <option value="checkbox">Checkbox</option>
                  </select>
                  <Button type="button" size="icon" variant="ghost" onClick={() => removeField(idx)} data-testid="form-template-remove-field-button">
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button onClick={submitForm} data-testid="form-template-submit-button">Create Template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!assignTarget} onOpenChange={(v) => !v && setAssignTarget(null)}>
        <DialogContent data-testid="assign-form-dialog">
          <DialogHeader>
            <DialogTitle>Assign &quot;{assignTarget?.title}&quot; to Student</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Student</Label>
            <select className="w-full h-11 rounded-[var(--radius)] border bg-background px-3 text-sm" value={assignStudentId} onChange={(e) => setAssignStudentId(e.target.value)} data-testid="assign-form-student-select">
              <option value="">Select student</option>
              {students.map((s) => (
                <option key={s.id} value={s.id}>{s.full_name}</option>
              ))}
            </select>
          </div>
          <DialogFooter>
            <Button onClick={submitAssign} data-testid="assign-form-submit-button">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
